import { useMemo } from 'react';
import Prism from 'prismjs';
import 'prismjs/components/prism-markup';
import 'prismjs/themes/prism-tomorrow.css';

type CodeLanguage = 'tailwind';

interface CodeBlockProps {
  code: string;
  language: CodeLanguage;
}

const prismLanguageMap: Record<CodeLanguage, string> = {
  tailwind: 'markup',
};

function getGrammar(language: string): Prism.Grammar {
  const grammars = Prism.languages as Record<string, Prism.Grammar | undefined>;
  return grammars[language] ?? Prism.languages.markup;
}

export function CodeBlock({ code, language }: CodeBlockProps) {
  const prismLanguage = prismLanguageMap[language];

  const highlightedCode = useMemo(() => {
    if (!code) {
      return '';
    }

    return Prism.highlight(code, getGrammar(prismLanguage), prismLanguage);
  }, [code, prismLanguage]);

  return (
    <pre
      className="m-0 overflow-x-auto bg-slate-950 p-4 text-sm leading-6 text-slate-100"
      tabIndex={0}
      data-ai-id="item-code-block"
    >
      {highlightedCode ? (
        <code
          className={`language-${prismLanguage}`}
          dangerouslySetInnerHTML={{ __html: highlightedCode }}
          data-ai-id="item-code-block-content"
        />
      ) : (
        <code className="text-slate-400" data-ai-id="item-code-block-empty">
          No code available.
        </code>
      )}
    </pre>
  );
}
